/**
 * Firebase Auth + Firestore persistence — optional by design.
 *
 * When the VITE_FIREBASE_* env vars are present (see docs/firebase-auth.md)
 * players can sign up / sign in and their profile (pilot name + ship layout)
 * lives in Firestore under users/{uid}. Without config, everything degrades
 * to guest mode (localStorage only) and the firebase SDK is never even
 * downloaded — all imports below are dynamic.
 */
import {
  proceduralAssetIssues,
  validateHealth,
  validateInventory,
  validateLayout,
  validateStyle,
  validateCharacterRecord,
  type CharacterStyle,
  type HealthState,
  type InventoryState,
  type ProceduralAssetV1,
  type ShipLayout,
  type CharacterRecordV1,
} from "@hyperlight/shared";

/** Emulator routing is accepted only by Vite's development build. A production
 *  bundle cannot be redirected by a stray environment variable. */
export const firebaseEmulatorMode = import.meta.env.DEV
  && import.meta.env.VITE_FIREBASE_EMULATORS === "1";
const emulatorProjectId = (import.meta.env.VITE_FIREBASE_EMULATOR_PROJECT_ID as string | undefined)
  || "demo-hyperlight";
const cfg = firebaseEmulatorMode ? {
  apiKey: "demo-hyperlight",
  authDomain: `${emulatorProjectId}.firebaseapp.com`,
  projectId: emulatorProjectId,
  appId: "1:000000000000:web:demo-hyperlight",
} : {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string | undefined,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string | undefined,
};

/** True when a Firebase project is configured at build time. */
export const authAvailable = Boolean(cfg.apiKey && cfg.authDomain && cfg.projectId && cfg.appId);

export interface AuthUser {
  uid: string;
  email: string | null;
  emailVerified: boolean;
}

export interface Profile {
  /** Server-owned identity/onboarding record; null means creation is required. */
  character: CharacterRecordV1 | null;
  name: string | null;
  /** Fast-boot mirror of the ACTIVE ship (the hangar lives in a subcollection). */
  ship: ShipLayout | null;
  /** Client-clock epoch ms of the last ship save (for staleness checks). */
  shipUpdatedAt: number;
  /** Which hangar ship (users/{uid}/ships doc id) is currently flown. */
  activeShipId: string | null;
  activeShipName: string | null;
  /** Which locker outfit (users/{uid}/outfits doc id) is currently worn. */
  activeOutfitId: string | null;
  activeOutfitName: string | null;
  /** Last known flight state, for resuming where the player left off. */
  system: string | null;
  /** C4: the last LATTICE system visited — where RETURN leads when the pilot
   *  logs back in inside their home pocket. */
  lastLattice: string | null;
  pos: [number, number, number] | null;
  quat: [number, number, number, number] | null;
  /**
   * Body (def.id) the ship `pos` is anchored to — an offset from that body's
   * ORBITAL CENTRE (no spin: a ship in space isn't glued to the turning
   * surface), so resume tracks the body as it orbits instead of stranding the
   * ship where the body USED to be. `null` = `pos` is absolute system space
   * (logged out in deep space, or a legacy save). `quat` is always absolute.
   */
  posBody: string | null;
  /**
   * On-foot state at logout: character pose, ship parked at pos/quat.
   * `body` is the stable def id of the planet/moon stood on, and `p`/`q` are in
   * that body's LOCAL (orbiting + spinning) frame — so resume lands on the same
   * spot however far the body has since moved. `body: null` = legacy save whose
   * `p`/`q` are absolute system space (restored via nearest-body fallback).
   */
  foot: {
    body: string | null;
    p: [number, number, number];
    q: [number, number, number, number];
  } | null;
  /**
   * LANDED-ship anchor (vehicle-tiers V1, the grounding point): the parked
   * ship's position in the stood-on body's LOCAL (orbiting + spinning) frame.
   * ⚠ Deliberately NOT `posBody`'s centre-only anchor — that has no spin term
   * (correct for a ship in space, which is not glued to the turning surface),
   * so a landed hull persisted through it migrates across the planet between
   * sessions as the planet rotates under it. Landed ships get the `foot`
   * treatment. Null = the ship was not parked at logout.
   */
  shipLand: { body: string; p: [number, number, number] } | null;
  /** RPG chassis state (validated on load; null = fresh starter kit). */
  rpg: { health: HealthState; inventory: InventoryState; fuelKit?: boolean } | null;
  /** Installed ship modules (phase 7 upgrades). */
  upgrades: { boost: boolean; pulse: boolean; mining: boolean };
  /** The pilot's chosen home WORLD (any planet, not just a system). */
  homeWorld: { system: string; planet: number } | null;
  /** Character appearance (suit locker); null = default style. */
  style: CharacterStyle | null;
}

/** A saved ship in the player's hangar (users/{uid}/ships/{id}). */
export interface ShipDoc {
  id: string;
  name: string;
  layout: ShipLayout;
  updatedAt: number;
}

/** A saved outfit in the player's locker (users/{uid}/outfits/{id}). */
export interface OutfitDoc {
  id: string;
  name: string;
  style: CharacterStyle;
  updatedAt: number;
}

type FirebaseBundle = {
  auth: import("firebase/auth").Auth;
  fs: import("firebase/firestore").Firestore;
  authMod: typeof import("firebase/auth");
  fsMod: typeof import("firebase/firestore");
};

let bundle: Promise<FirebaseBundle> | null = null;

function fb(): Promise<FirebaseBundle> {
  if (!authAvailable) return Promise.reject(new Error("firebase not configured"));
  bundle ??= (async () => {
    const [{ initializeApp }, authMod, fsMod] = await Promise.all([
      import("firebase/app"),
      import("firebase/auth"),
      import("firebase/firestore"),
    ]);
    const app = initializeApp({
      apiKey: cfg.apiKey!,
      authDomain: cfg.authDomain!,
      projectId: cfg.projectId!,
      appId: cfg.appId!,
    });
    const auth = authMod.getAuth(app);
    // Browser Firestore defaults to an in-memory cache. A position checkpoint
    // made during pagehide can therefore disappear with the page before the
    // network acknowledges it. Keep the mutation queue in IndexedDB instead;
    // Firestore will replay pending writes on the next visit. Multi-tab mode
    // avoids one game/title tab preventing another from opening the cache.
    const fs = fsMod.initializeFirestore(app, {
      localCache: fsMod.persistentLocalCache({
        tabManager: fsMod.persistentMultipleTabManager(),
      }),
    });
    if (firebaseEmulatorMode) {
      authMod.connectAuthEmulator(auth, "http://127.0.0.1:9160", { disableWarnings: true });
      fsMod.connectFirestoreEmulator(fs, "127.0.0.1", 8160);
      console.info(`[firebase] local Auth + Firestore emulators (${emulatorProjectId})`);
    }
    return { auth, fs, authMod, fsMod };
  })();
  // A failed dynamic import / emulator startup must be retryable from the UI.
  // Promise rejections are otherwise cached forever in `bundle`.
  return bundle.catch((err) => {
    bundle = null;
    throw err;
  });
}

/** Subscribe to auth state after explicitly selecting durable browser persistence.
 *  No-op (immediate null) when unconfigured. */
export function watchAuth(
  cb: (user: AuthUser | null) => void,
  onError?: (error: unknown) => void,
): void {
  if (!authAvailable) {
    cb(null);
    return;
  }
  void fb()
    .then(async ({ auth, authMod }) => {
      await authMod.setPersistence(auth, authMod.browserLocalPersistence);
      authMod.onAuthStateChanged(
        auth,
        (u) => cb(u ? toAuthUser(u) : null),
        (err) => onError?.(err),
      );
    })
    .catch((err) => onError?.(err));
}

function toAuthUser(u: import("firebase/auth").User): AuthUser {
  return { uid: u.uid, email: u.email, emailVerified: u.emailVerified };
}

export async function signUp(email: string, password: string): Promise<AuthUser> {
  const { auth, authMod } = await fb();
  const cred = await authMod.createUserWithEmailAndPassword(auth, email, password);
  await authMod.sendEmailVerification(cred.user);
  return toAuthUser(cred.user);
}

export async function signIn(email: string, password: string): Promise<AuthUser> {
  const { auth, authMod } = await fb();
  const cred = await authMod.signInWithEmailAndPassword(auth, email, password);
  return toAuthUser(cred.user);
}

/** DEV ONBOARDING KICKSTART (emulator builds only — the login button, user call
 *  2026-07-23): mint a FRESH, already-VERIFIED user in one call. Creates a unique
 *  dev-<ts>@kickoff.dev account, flips emailVerified through the emulator's admin
 *  surface (`Authorization: Bearer owner` — the Auth emulator's owner credential),
 *  then refreshes the session token so the SERVER sees email_verified=true too.
 *  No verification email is ever sent. Throws outside emulator mode. */
export async function devMintVerifiedUser(): Promise<AuthUser> {
  if (!firebaseEmulatorMode) throw new Error("dev mint is emulator-only");
  const { auth, authMod } = await fb();
  const email = `dev-${Date.now().toString(36)}@kickoff.dev`;
  const cred = await authMod.createUserWithEmailAndPassword(auth, email, "kickoff-dev-1");
  const res = await fetch("http://127.0.0.1:9160/identitytoolkit.googleapis.com/v1/accounts:update", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: "Bearer owner" },
    body: JSON.stringify({ localId: cred.user.uid, emailVerified: true }),
  });
  if (!res.ok) throw new Error(`emulator verify flip failed (${res.status})`);
  await cred.user.reload();
  await cred.user.getIdToken(true); // fresh claims — the join gate reads email_verified
  return toAuthUser(cred.user);
}

/** Re-send the verification email to the current user. */
export async function resendVerification(): Promise<void> {
  const { auth, authMod } = await fb();
  if (auth.currentUser) await authMod.sendEmailVerification(auth.currentUser);
}

/**
 * Re-fetch the current user from Firebase (picks up emailVerified changes
 * after the player clicks the link). Returns the refreshed user, or null.
 */
export async function refreshUser(): Promise<AuthUser | null> {
  const { auth } = await fb();
  if (!auth.currentUser) return null;
  await auth.currentUser.reload();
  // Force a fresh ID token so the server sees email_verified=true too.
  await auth.currentUser.getIdToken(true);
  return toAuthUser(auth.currentUser);
}

export async function signOutUser(): Promise<void> {
  if (!authAvailable) return;
  const { auth, authMod } = await fb();
  await authMod.signOut(auth);
}

/** Current user's ID token for server-side verification, or null. */
export async function getIdToken(): Promise<string | null> {
  if (!authAvailable) return null;
  try {
    const { auth } = await fb();
    return (await auth.currentUser?.getIdToken()) ?? null;
  } catch {
    return null;
  }
}

/**
 * Load one character's client-written profile (users/{uid}/characters/{cid}).
 * `record` is the authoritative identity already fetched over HTTP — the doc
 * read only supplies the client-checkpointed fields (resume pose, ship
 * mirror, pointers); a missing doc is simply a character that has never
 * checkpointed (fresh creation), not an error.
 */
export async function loadCharacterProfile(record: CharacterRecordV1): Promise<Profile> {
  const { auth, fs, fsMod } = await fb();
  const uid = auth.currentUser?.uid;
  noteUid(uid ?? null);
  const empty: Profile = {
    character: record,
    name: record.name,
    ship: null,
    shipUpdatedAt: 0,
    activeShipId: null,
    activeShipName: null,
    activeOutfitId: null,
    activeOutfitName: null,
    system: null,
    lastLattice: null,
    pos: null,
    quat: null,
    posBody: null,
    foot: null,
    shipLand: null,
    rpg: null,
    upgrades: { boost: false, pulse: false, mining: false },
    homeWorld: null,
    style: null,
  };
  if (!uid) return empty;
  const snap = await fsMod.getDoc(fsMod.doc(fs, "users", uid, "characters", record.id));
  if (!snap.exists()) return empty;
  const data = snap.data();
  const vec = (v: unknown, len: number): number[] | null =>
    Array.isArray(v) && v.length === len && v.every((n) => Number.isFinite(n))
      ? (v as number[])
      : null;
  const footData = data.foot as { body?: unknown; p?: unknown; q?: unknown } | null | undefined;
  const footP = footData ? vec(footData.p, 3) : null;
  const footQ = footData ? vec(footData.q, 4) : null;
  const footBody = footData && typeof footData.body === "string" ? footData.body : null;
  const rpgData = data.rpg as { health?: unknown; inventory?: unknown; fuelKit?: unknown } | undefined;
  const rpgInv = rpgData ? validateInventory(rpgData.inventory) : null;
  return {
    character: record,
    name: record.name,
    ship: validateLayout(data.ship),
    shipUpdatedAt: typeof data.shipUpdatedAt === "number" ? data.shipUpdatedAt : 0,
    activeShipId: typeof data.activeShipId === "string" ? data.activeShipId : null,
    activeShipName: typeof data.activeShipName === "string" ? data.activeShipName : null,
    activeOutfitId: typeof data.activeOutfitId === "string" ? data.activeOutfitId : null,
    activeOutfitName: typeof data.activeOutfitName === "string" ? data.activeOutfitName : null,
    system: typeof data.system === "string" ? data.system : null,
    lastLattice: typeof data.lastLattice === "string" ? data.lastLattice : null,
    pos: vec(data.pos, 3) as Profile["pos"],
    quat: vec(data.quat, 4) as Profile["quat"],
    posBody: typeof data.posBody === "string" ? data.posBody : null,
    foot:
      footP && footQ
        ? {
            body: footBody,
            p: footP as [number, number, number],
            q: footQ as [number, number, number, number],
          }
        : null,
    shipLand: (() => {
      const sl = data.shipLand as { body?: unknown; p?: unknown } | null | undefined;
      const p = sl ? vec(sl.p, 3) : null;
      return sl && typeof sl.body === "string" && p
        ? { body: sl.body, p: p as [number, number, number] }
        : null;
    })(),
    rpg: rpgInv
      ? { health: validateHealth(rpgData?.health), inventory: rpgInv, fuelKit: rpgData?.fuelKit === true }
      : null,
    style: validateStyle(data.style),
    upgrades: {
      boost: (data.upgrades as Record<string, unknown> | undefined)?.boost === true,
      pulse: (data.upgrades as Record<string, unknown> | undefined)?.pulse === true,
      mining: (data.upgrades as Record<string, unknown> | undefined)?.mining === true,
    },
    homeWorld: (() => {
      const hw = data.homeWorld as Record<string, unknown> | undefined;
      return hw && typeof hw.system === "string" && typeof hw.planet === "number"
        ? { system: hw.system, planet: hw.planet }
        : null;
    })(),
  };
}

export class CharacterApiError extends Error {
  constructor(readonly code: string, readonly status: number) {
    super(code);
  }
}

async function characterApi(
  path: "/api/characters" | "/api/characters/activate" | "/api/characters/stage"
    | "/api/characters/delete",
  init?: RequestInit,
): Promise<Record<string, unknown>> {
  const token = await getIdToken();
  if (!token) throw new CharacterApiError("unauthorized", 401);
  const response = await fetch(path, {
    ...init,
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });
  let parsed = true;
  const body = await response.json().catch(() => (parsed = false, {})) as Record<string, unknown>;
  if (!response.ok) throw new CharacterApiError(
    typeof body.error === "string" ? body.error
      // A non-JSON error body means the request never reached the API — almost
      // always a proxy misroute (e.g. nginx answering 405 for a POST that hit
      // the static SPA fallback), not the character service actually failing.
      : parsed ? "character-service-unavailable" : "api-unreachable",
    response.status,
  );
  return body;
}

function recordOf(body: Record<string, unknown>): CharacterRecordV1 | null {
  return validateCharacterRecord(body.character, currentUid() ?? undefined);
}

function rosterOf(body: Record<string, unknown>): CharacterRecordV1[] {
  const raw = Array.isArray(body.characters) ? body.characters : [];
  const uid = currentUid() ?? undefined;
  return raw
    .map((entry) => validateCharacterRecord(entry, uid))
    .filter((entry): entry is CharacterRecordV1 => entry !== null);
}

/** The account's full character roster, server-authoritative. */
export async function listCharacters(): Promise<CharacterRecordV1[]> {
  return rosterOf(await characterApi("/api/characters"));
}

/** Permanently delete one character; resolves to the roster that remains.
 *  Irreversible — the caller owns the confirmation. */
export async function deleteCharacter(cid: string): Promise<CharacterRecordV1[]> {
  return rosterOf(await characterApi("/api/characters/delete", {
    method: "POST",
    body: JSON.stringify({ id: cid }),
  }));
}

/** Re-read one character from the authoritative roster. */
export async function loadCharacter(cid: string): Promise<CharacterRecordV1 | null> {
  const characters = await listCharacters();
  return characters.find((entry) => entry.id === cid) ?? null;
}

export async function createCharacter(
  name: string,
  style: CharacterStyle,
  /** SOUL-kickoff opt-in: start the staged prologue instead of the homecoming intro. */
  kickoff = false,
): Promise<CharacterRecordV1 | null> {
  return recordOf(await characterApi("/api/characters", {
    method: "POST",
    body: JSON.stringify({ name, style, kickoff }),
  }));
}

export async function activateCharacter(cid: string): Promise<CharacterRecordV1 | null> {
  return recordOf(await characterApi("/api/characters/activate", {
    method: "POST",
    body: JSON.stringify({ id: cid }),
  }));
}

/** Report SOUL-kickoff progress; the server validates the strict stage order.
 *  Rejections resolve null (a relog re-derives the right beat from the record). */
export function advanceCharacterStage(cid: string, to: string): Promise<CharacterRecordV1 | null> {
  return characterApi("/api/characters/stage", {
    method: "POST",
    body: JSON.stringify({ id: cid, to }),
  }).then(recordOf).catch(() => null);
}

export async function setActiveShipId(id: string | null, name?: string): Promise<void> {
  await save({ activeShipId: id, activeShipName: name ?? null });
}

// --- Hangar: users/{uid}/ships subcollection ---

export async function listShipDocs(): Promise<ShipDoc[]> {
  const { auth, fs, fsMod } = await fb();
  const uid = auth.currentUser?.uid;
  if (!uid) return [];
  const snap = await fsMod.getDocs(fsMod.collection(fs, "users", uid, "ships"));
  const out: ShipDoc[] = [];
  for (const d of snap.docs) {
    const data = d.data();
    const layout = validateLayout(data.layout);
    if (!layout) continue;
    out.push({
      id: d.id,
      name: typeof data.name === "string" && data.name ? data.name : "Unnamed Ship",
      layout,
      updatedAt: typeof data.updatedAt === "number" ? data.updatedAt : 0,
    });
  }
  out.sort((a, b) => b.updatedAt - a.updatedAt);
  return out;
}

export async function createShipDoc(
  name: string,
  layout: ShipLayout,
  updatedAt: number,
): Promise<string> {
  const { auth, fs, fsMod } = await fb();
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("not signed in");
  const ref = await fsMod.addDoc(fsMod.collection(fs, "users", uid, "ships"), {
    name,
    layout,
    updatedAt,
    createdAt: updatedAt,
  });
  return ref.id;
}

export async function updateShipDoc(
  id: string,
  patch: { name?: string; layout?: ShipLayout },
  updatedAt: number,
): Promise<void> {
  const { auth, fs, fsMod } = await fb();
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  await fsMod.setDoc(
    fsMod.doc(fs, "users", uid, "ships", id),
    { ...patch, updatedAt },
    { merge: true },
  );
}

export async function deleteShipDoc(id: string): Promise<void> {
  const { auth, fs, fsMod } = await fb();
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  await fsMod.deleteDoc(fsMod.doc(fs, "users", uid, "ships", id));
}

/** A saved procedural asset from the asset lab (users/{uid}/assets/{id}). */
export interface AssetDoc {
  id: string;
  name: string;
  kind: string;
  asset: ProceduralAssetV1;
  updatedAt: number;
}

export async function listAssetDocs(): Promise<AssetDoc[]> {
  const { auth, fs, fsMod } = await fb();
  const uid = auth.currentUser?.uid;
  if (!uid) return [];
  const snap = await fsMod.getDocs(fsMod.collection(fs, "users", uid, "assets"));
  const out: AssetDoc[] = [];
  for (const d of snap.docs) {
    const data = d.data();
    if (proceduralAssetIssues(data.asset).length) continue;
    const asset = data.asset as ProceduralAssetV1;
    out.push({
      id: d.id,
      name: typeof data.name === "string" && data.name ? data.name : asset.name,
      kind: typeof data.kind === "string" ? data.kind : asset.kind,
      asset,
      updatedAt: typeof data.updatedAt === "number" ? data.updatedAt : 0,
    });
  }
  out.sort((a, b) => b.updatedAt - a.updatedAt);
  return out;
}

export async function createAssetDoc(asset: ProceduralAssetV1, updatedAt: number): Promise<string> {
  const { auth, fs, fsMod } = await fb();
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("not signed in");
  const ref = await fsMod.addDoc(fsMod.collection(fs, "users", uid, "assets"), {
    name: asset.name,
    kind: asset.kind,
    asset,
    updatedAt,
    createdAt: updatedAt,
  });
  return ref.id;
}

export async function updateAssetDoc(id: string, asset: ProceduralAssetV1, updatedAt: number): Promise<void> {
  const { auth, fs, fsMod } = await fb();
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  await fsMod.setDoc(
    fsMod.doc(fs, "users", uid, "assets", id),
    { name: asset.name, kind: asset.kind, asset, updatedAt },
    { merge: true },
  );
}

export async function deleteAssetDoc(id: string): Promise<void> {
  const { auth, fs, fsMod } = await fb();
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  await fsMod.deleteDoc(fsMod.doc(fs, "users", uid, "assets", id));
}

/**
 * Persist the player's last position for resume-on-login. `pos`/`quat` are the
 * SHIP pose (in flight or parked); `posBody`, when set, marks `pos` as an offset
 * from that body's orbital centre (see Profile.posBody). `foot` carries the
 * character pose when the player logged out disembarked, null otherwise — and
 * when present its `p`/`q` are in the stood-on body's LOCAL frame (Profile.foot).
 */
export async function savePosition(
  system: string,
  pos: [number, number, number],
  quat: [number, number, number, number],
  posBody: string | null = null,
  foot: {
    body: string | null;
    p: [number, number, number];
    q: [number, number, number, number];
  } | null = null,
  lastLattice: string | null = null,
  shipLand: { body: string; p: [number, number, number] } | null = null,
): Promise<void> {
  await save({ system, pos, quat, posBody, foot, lastLattice, shipLand });
}

/** The signed-in uid, or null — resolved from the cached auth module. */
let knownUid: string | null = null;
export function currentUid(): string | null {
  return knownUid;
}
export function noteUid(uid: string | null): void {
  knownUid = uid;
  if (uid === null) knownCid = null;
}

/** The active character id (`c<slot>`) this session plays, or null before the
 *  select screen picks one. Every per-character checkpoint routes through it. */
let knownCid: string | null = null;
export function currentCharacterId(): string | null {
  return knownCid;
}
export function noteCharacter(cid: string | null): void {
  knownCid = cid;
}

export async function saveHomeWorld(hw: { system: string; planet: number } | null): Promise<void> {
  await save({ homeWorld: hw });
}

export async function saveUpgrades(upgrades: {
  boost: boolean;
  pulse: boolean;
  mining: boolean;
}): Promise<void> {
  await save({ upgrades });
}

export async function saveRpg(
  health: HealthState,
  inventory: InventoryState,
  fuelKit = false,
): Promise<void> {
  await save({ rpg: { health, inventory, fuelKit } });
}

/** Persist the character appearance. */
export async function saveStyle(
  style: CharacterStyle,
  outfit?: { id: string; name: string } | null,
): Promise<void> {
  await save({ style, ...(outfit === undefined ? {} : {
    activeOutfitId: outfit?.id ?? null, activeOutfitName: outfit?.name ?? null,
  }) });
}

// --- Outfit locker: users/{uid}/outfits subcollection (the hangar pattern) ---

// --- Discovered systems: one tiny doc per system the pilot has visited.
// Doc ids are the system id with ":" flattened ("-1:0:2" → "-1_0_2"). ---

export async function listDiscovered(): Promise<{ id: string; at: number }[]> {
  const { auth, fs, fsMod } = await fb();
  const uid = auth.currentUser?.uid;
  if (!uid) return [];
  const snap = await fsMod.getDocs(fsMod.collection(fs, "users", uid, "discovered"));
  return snap.docs.map((d) => ({
    id: d.id.replace(/_/g, ":"),
    at: typeof d.data().at === "number" ? (d.data().at as number) : 0,
  }));
}

export async function addDiscovered(systemId: string): Promise<void> {
  const { auth, fs, fsMod } = await fb();
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  await fsMod.setDoc(
    fsMod.doc(fs, "users", uid, "discovered", systemId.replace(/:/g, "_")),
    { at: Date.now() },
  );
}

export async function listOutfitDocs(): Promise<OutfitDoc[]> {
  const { auth, fs, fsMod } = await fb();
  const uid = auth.currentUser?.uid;
  if (!uid) return [];
  const snap = await fsMod.getDocs(fsMod.collection(fs, "users", uid, "outfits"));
  const out: OutfitDoc[] = [];
  for (const d of snap.docs) {
    const data = d.data();
    const style = validateStyle(data.style);
    if (!style) continue;
    out.push({
      id: d.id,
      name: typeof data.name === "string" && data.name ? data.name : "Unnamed Outfit",
      style,
      updatedAt: typeof data.updatedAt === "number" ? data.updatedAt : 0,
    });
  }
  out.sort((a, b) => b.updatedAt - a.updatedAt);
  return out;
}

export async function createOutfitDoc(
  name: string,
  style: CharacterStyle,
  updatedAt: number,
): Promise<string> {
  const { auth, fs, fsMod } = await fb();
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("not signed in");
  const ref = await fsMod.addDoc(fsMod.collection(fs, "users", uid, "outfits"), {
    name,
    style,
    updatedAt,
    createdAt: updatedAt,
  });
  return ref.id;
}

export async function updateOutfitDoc(
  id: string,
  patch: { name?: string; style?: CharacterStyle },
  updatedAt: number,
): Promise<void> {
  const { auth, fs, fsMod } = await fb();
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  await fsMod.setDoc(
    fsMod.doc(fs, "users", uid, "outfits", id),
    { ...patch, updatedAt },
    { merge: true },
  );
}

export async function deleteOutfitDoc(id: string): Promise<void> {
  const { auth, fs, fsMod } = await fb();
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  await fsMod.deleteDoc(fsMod.doc(fs, "users", uid, "outfits", id));
}

/** Which locker outfit is currently worn (mirrors setActiveShipId). */
export async function setActiveOutfitId(id: string | null, name?: string): Promise<void> {
  await save({ activeOutfitId: id, activeOutfitName: name ?? null });
}

/** Persist a ship layout with its save timestamp (client clock, epoch ms). */
export async function saveShip(ship: ShipLayout, shipUpdatedAt: number): Promise<void> {
  await save({ ship, shipUpdatedAt });
}

async function save(patch: Record<string, unknown>): Promise<void> {
  const { auth, fs, fsMod } = await fb();
  const uid = auth.currentUser?.uid;
  // Everything checkpointed here is PER-CHARACTER state (resume pose, style,
  // ship mirror, pointers) — with no active character there is nothing it
  // could correctly describe, so the write is dropped, not misfiled.
  if (!uid || !knownCid) return;
  await fsMod.setDoc(
    fsMod.doc(fs, "users", uid, "characters", knownCid),
    { ...patch, updatedAt: fsMod.serverTimestamp() },
    { merge: true },
  );
}

/** Human-readable message for common Firebase auth error codes. */
export function authErrorMessage(err: unknown): string {
  const code = (err as { code?: string })?.code ?? "";
  switch (code) {
    case "auth/invalid-email":
      return "invalid email address";
    case "auth/email-already-in-use":
      return "an account already exists for that email";
    case "auth/weak-password":
      return "password too weak (6+ characters)";
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "wrong email or password";
    case "auth/too-many-requests":
      return "too many attempts — try again later";
    default:
      return "sign-in failed — check your connection";
  }
}
